import { useState } from 'react';
import type { PartnerBehaviour, PartnerNetworkType } from '@dru/types';
import {
  Alert, Button, Card, CardBody, CardHeader, ConfirmDialog, SelectField,
  StatusBadge, TextField, useIdempotencyKey, useToast,
} from '@dru/ui';
import { ApiError } from '@dru/api-client';
import { api } from '../../../lib/api';
import { useQuery } from '../../../lib/useQuery';
import { errorMessage, useAuth } from '../../../lib/auth';
import { holds } from '../../../layouts/ConsoleShell';

/**
 * The partner network type catalogue.
 *
 * The registration form used to offer three choices compiled into it, so
 * "Collection Centre" needed a deployment. These are rows now.
 *
 * The `behaviour` column is the part worth reading twice. A type is how the
 * business DESCRIBES a venue; `behaviour` is what the platform DOES with one —
 * routing, capacity and the appointment card read it. A collection centre and
 * a pathology lab are different relationships and both behave as LABORATORY.
 * Choosing a type on the registration form therefore sets both, which is why
 * the behaviour is shown here rather than hidden: it is the half with
 * consequences.
 *
 * Nothing is deleted while it is in use. A retired type is deactivated — it
 * leaves the registration form and stays readable on the venues that already
 * carry it, exactly like the clinical catalogue.
 */

const BEHAVIOUR_HINT: Record<PartnerBehaviour, string> = {
  HOSPITAL: 'Runs examination stations',
  LABORATORY: 'Results samples or studies',
  BOTH: 'Runs stations and results its own samples',
};

export function PartnerNetworkTypesCard() {
  const { permissions } = useAuth();
  // The API's own checks: adding a type is `partner.create`, changing or
  // deleting one `partner.update`. A reader without them sees the catalogue.
  const canAdd = holds(permissions, 'partner.create');
  const canChange = holds(permissions, 'partner.update');
  const types = useQuery<{ items: PartnerNetworkType[] }>(
    () => api.get('/partner-network-types?includeInactive=true'), [],
  );
  const [adding, setAdding] = useState(false);
  const [deleting, setDeleting] = useState<PartnerNetworkType | null>(null);
  const [error, setError] = useState<string | null>(null);
  const toast = useToast();

  const rows = types.data?.items ?? [];

  async function toggle(t: PartnerNetworkType) {
    setError(null);
    try {
      await api.patch(`/partner-network-types/${t.id}`, { isActive: !t.isActive });
      toast.toast('success', t.isActive
        ? `${t.name} deactivated. It will not appear when registering a venue.`
        : `${t.name} is available again.`);
      types.reload();
    } catch (e) { setError(errorMessage(e)); }
  }

  return (
    <Card>
      <CardHeader
        title="Network partner types"
        hint="How the business describes a lab or hospital. The registration form offers the active ones."
      />
      <CardBody className="space-y-4">
        {types.error && <Alert tone="danger">{errorMessage(types.error)}</Alert>}
        {error && <Alert tone="danger">{error}</Alert>}

        {rows.length === 0 && !types.loading && (
          <p className="text-small text-neutral-500">
            No types yet. A network partner cannot be registered until there is at least one.
          </p>
        )}

        <div className="space-y-2">
          {rows.map((t) => (
            <div
              key={t.id}
              className="flex flex-wrap items-start justify-between gap-3 rounded-input
                         border border-neutral-200 px-3 py-2"
            >
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-medium text-neutral-900">{t.name}</span>
                  <span className="font-mono text-caption text-neutral-500">{t.code}</span>
                  {!t.isActive && <StatusBadge tone="neutral">Deactivated</StatusBadge>}
                </div>
                {t.description && <p className="mt-0.5 text-xs text-neutral-600">{t.description}</p>}
                <p className="mt-0.5 text-xs text-neutral-500">
                  Routed as: {BEHAVIOUR_HINT[t.behaviour].toLowerCase()}
                </p>
                {!!t.partnersUsing && (
                  <p className="mt-0.5 text-caption text-neutral-500">
                    {t.partnersUsing} partner{t.partnersUsing === 1 ? ' carries' : 's carry'} this type
                  </p>
                )}
              </div>
              {canChange && (
              <div className="flex shrink-0 items-center gap-1">
                <Button size="sm" variant="ghost" onClick={() => toggle(t)} aria-label={`${t.isActive ? 'Deactivate' : 'Reactivate'} ${t.name}`}>
                  {t.isActive ? 'Deactivate' : 'Reactivate'}
                </Button>
                {/* Deleting is only offered for a type nothing points at. The API
                    refuses the rest, but offering a button that always fails is
                    worse than not offering it. */}
                {!t.partnersUsing && (
                  <Button
                    size="sm" variant="ghost"
                    className="text-danger-600 hover:bg-danger-100"
                    onClick={() => setDeleting(t)}
                    aria-label={`Delete ${t.name}`}
                  >
                    Delete
                  </Button>
                )}
              </div>
              )}
            </div>
          ))}
        </div>

        {canAdd && (
          <div className="flex justify-end">
            <Button size="sm" onClick={() => setAdding(true)}>Add a type</Button>
          </div>
        )}
      </CardBody>

      {adding && (
        <AddTypeDialog
          onClose={() => setAdding(false)}
          onDone={(name) => {
            setAdding(false);
            toast.toast('success', `${name} added.`);
            types.reload();
          }}
        />
      )}

      {deleting && (
        <ConfirmDialog
          title="Delete this type?"
          what={<>
            <strong>{deleting.name}</strong> will be removed from the catalogue.
          </>}
          affects="No partner carries it, so nothing else changes. Deactivate instead if you
                   may want it back."
          confirmLabel="Delete"
          tone="danger"
          onConfirm={async () => {
            await api.del(`/partner-network-types/${deleting.id}`);
            toast.toast('success', `${deleting.name} deleted.`);
            setDeleting(null);
            types.reload();
          }}
          onCancel={() => setDeleting(null)}
        />
      )}
    </Card>
  );
}

function AddTypeDialog({ onClose, onDone }: {
  onClose: () => void; onDone: (name: string) => void;
}) {
  const [form, setForm] = useState({
    code: '', name: '', behaviour: 'LABORATORY' as PartnerBehaviour,
    description: '', sortOrder: '100',
  });
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  // The endpoint is @Idempotent() and refuses a call without a key. A double
  // submit would otherwise be a second type with the same code — refused by the
  // unique index, but as a confusing 409 rather than a no-op.
  const idem = useIdempotencyKey();

  async function submit() {
    setError(null); setFieldErrors({});
    try {
      await api.post('/partner-network-types', {
        code: form.code.trim().toUpperCase(),
        name: form.name.trim(),
        behaviour: form.behaviour,
        ...(form.description.trim() ? { description: form.description.trim() } : {}),
        sortOrder: Number(form.sortOrder) || 100,
      }, idem.key());
      idem.rotate();
      onDone(form.name.trim());
    } catch (e) {
      if (e instanceof ApiError) setFieldErrors(e.fieldErrors);
      setError(errorMessage(e));
      // Rethrown so ConfirmDialog keeps itself open: closing over a failure
      // tells the operator the type was added.
      throw e;
    }
  }

  return (
    <ConfirmDialog
      title="Add a network partner type"
      what="A new option on the partner registration form."
      confirmLabel="Add type"
      onConfirm={submit}
      onCancel={onClose}
    >
      <div className="space-y-3">
        <TextField
          label="Code" value={form.code} mono autoFocus error={fieldErrors.code}
          hint="A stable identifier. Cannot be changed afterwards."
          onChange={(v) => setForm({ ...form, code: v.toUpperCase().replace(/[^A-Z0-9_-]/g, '') })}
        />
        <TextField
          label="Name" value={form.name} error={fieldErrors.name}
          hint="What an operator picks from the list — for example, Collection Centre."
          onChange={(v) => setForm({ ...form, name: v })}
        />

        <SelectField
          label="What the platform does with it"
          value={form.behaviour}
          onChange={(v) => setForm({ ...form, behaviour: v as PartnerBehaviour })}
          info="This drives routing, so it is not just a label. A partner registered under this type is sent work accordingly."
        >
          <option value="HOSPITAL">Runs examination stations (hospital)</option>
          <option value="LABORATORY">Results samples or studies (laboratory)</option>
          <option value="BOTH">Both</option>
        </SelectField>

        <TextField
          label="Description (optional)" value={form.description} error={fieldErrors.description}
          info="Shown under the choice on the registration form."
          onChange={(v) => setForm({ ...form, description: v })}
        />

        {error && <Alert tone="danger">{error}</Alert>}
      </div>
    </ConfirmDialog>
  );
}
